<template>
  <footer class="app-footer">
    <div class="footer-inner">
      <BackstageIcon name="white-logo" class="footer-logo" data-cy="footer-logo" />
      <div class="footer-nav-container" data-cy="footer-container">
        <ul class="footer-nav-list">
          <li>
            <ul class="footer-nav-list-col">
              <li>
                <router-link class="footer-link" data-cy="footer-home-link" :to="{ name: 'home' }">
                  {{ $t('footer.home') }}
                </router-link>
              </li>
              <li>
                <a
                  class="footer-link"
                  data-cy="footer-about-link"
                  :href="$t('footer.about_url').toString()"
                  target="_blank">
                  {{ $t('footer.about') }}
                </a>
              </li>
              <li>
                <a
                  class="footer-link"
                  data-cy="footer-off-stories-link"
                  :href="$t('footer.press_media_url').toString()"
                  target="_blank">
                  {{ $t('footer.press_media') }}
                </a>
              </li>
              <li>
                <router-link to="/contact-us">
                  <span class="footer-link" data-cy="footer-contact-us-link">
                    {{ $t('header.nav.contact') }}
                  </span>
                </router-link>
              </li>
              <li>
                <div class="locale-selector" data-cy="footer-locale-selector">
                  <VueSelect
                    :input-id="'locale-selector'"
                    v-model="selectedLocale"
                    :clearable="false"
                    :searchable="false"
                    :get-option-label="getOptionLabel"
                    :options="locales"
                    class="language-dropdown"
                    @update:modelValue="onChangeLocale">
                  </VueSelect>
                </div>
              </li>
            </ul>
          </li>

          <li>
            <ul class="footer-nav-list-col">
              <li>
                <a
                  class="footer-link"
                  data-cy="footer-sustainability-link"
                  :href="$t('footer.company_information_url').toString()"
                  target="_blank">
                  {{ $t('footer.company_information') }}
                </a>
              </li>
              <li>
                <a
                  class="footer-link"
                  data-cy="footer-privacy-policy-link"
                  :href="$t('footer.privacy_policy_url').toString()"
                  target="_blank">
                  {{ $t('footer.privacy_policy') }}
                </a>
              </li>
              <li>
                <a
                  id="ot-sdk-btn"
                  class="optanon-toggle-display footer-link"
                  data-cy="footer-consent-settings-link"
                  v-on:click="setConsentBannerSettings()">
                  {{ $t('footer.consent_settings') }}
                </a>
              </li>
              <li class="footer-country" data-cy="footer-country">
                <div class="footer-country-name">
                  {{ $t('footer.switzerland') }}
                </div>
                <backstage-icon name="swiss-flag" class="country-flag" data-cy="footer-country-flag" />
              </li>
              <li>
                <div class="theme-switcher" data-cy="footer-theme-switcher">
                  <span class="label">{{ user?.theme + ' mode' }}</span>
                  <label class="switch">
                    <input
                      name="theme-selector"
                      type="checkbox"
                      :checked="user?.theme === 'dark'"
                      v-on:change="onThemeChange" />
                    <span class="slider round" data-cy="footer-theme-toggle"></span>
                  </label>
                </div>
              </li>
              <li>
                <div class="footer-copyright" data-cy="footer-copyright">
                  © On {{ new Date().getFullYear() }} - v{{ appVersion }}
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <!-- <backstage-button class="report-bug-btn" variant="pale-pinkish" icon-left="triangle-warning">
        {{ $t('footer.report_bug') }}
      </backstage-button> -->
    </div>
  </footer>
</template>

<script lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { BackstageTheme } from '@/models/backstage-theme.enum';
import { UsersService } from '@/services/users.service';
import type { LocalizedString } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import VueSelect from 'vue-select';
import { useLocale } from '../composables/locale';
import { defaultMessages } from '../lang/default';
import { useUserStore } from '../stores/user';

export default defineComponent({
  name: 'app-footer',
  components: {
    VueSelect,
    BackstageIcon,
  },
  data() {
    const { userLanguage, userLocale } = useLocale();
    const userStore = useUserStore();
    const { userProfile } = storeToRefs(userStore);

    return {
      validLocales: defaultMessages.global.languages,
      usersService: new UsersService(),
      user: userProfile.value?.user,
      userLanguage,
      userLocale,
      userStore,
      selectedLocale: userProfile.value?.user.locale ?? 'en-US',
      appVersion: import.meta.env.VITE_APP_version,
    };
  },
  computed: {
    locales() {
      return Object.keys(this.validLocales)
        .filter((locale) => locale !== this.userLocale)
        .sort();
    },
  },
  methods: {
    onChangeLocale(locale: keyof LocalizedString) {
      if (this.user) {
        this.userStore.setUserLocale(locale);
      }
    },
    onThemeChange(event: Event) {
      if (this.user) {
        this.userStore.setUserTheme(
          (event.target as HTMLInputElement).checked ? BackstageTheme.dark : BackstageTheme.light,
        );
      }
    },
    getOptionLabel(locale: keyof LocalizedString): string {
      return this.validLocales[locale];
    },
    setConsentBannerSettings() {
      const oneTrustFct = document.createElement('script');

      oneTrustFct.setAttribute('type', 'text/javascript');
      oneTrustFct.innerText = 'function OptanonWrapper() {}';
      document.body.appendChild(oneTrustFct);
    },
  },
});
</script>

<style lang="scss">
#backstage-ui .app-footer {
  .v-select.languge-dropdown {
    width: 12.5rem;
  }

  .vs__actions,
  .vs__selected-options {
    padding: 0;
  }

  .vs__open-indicator {
    display: none;
    fill: var(--dark-blue-gray-color);
  }

  .vs__actions {
    width: 9px;
    background: url('@/assets/img//chevron-right.svg') no-repeat center;
    background-size: contain;
    transform: rotate(90deg);
    margin-left: 8px;
    transition: transform 300ms;
  }

  .vs--open .vs__actions {
    transform: rotate(-90deg);
  }

  .vs--open .vs__dropdown-toggle {
    position: relative;
    border: 0 !important;
  }

  .vs__dropdown-toggle {
    border: 0 !important;
    height: 21px;
    padding: 0;
  }

  .vs__selected {
    color: var(--white-color);
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 150%;
    padding: 0;
    margin: 0;
    height: 21px;
  }

  .vs__search {
    height: 21px;
    margin: 0;
  }

  .vs__dropdown-menu {
    height: 25rem;
    width: 12.5rem;
    background: var(--primary-bg-color);
    top: calc(100% - 24.625rem);
  }

  .vs__dropdown-option {
    color: var(--primary-text-color);
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 150%;
  }
  .vs__dropdown-option--highlight {
    background-color: var(--hover-field-bg-color);
  }
}
</style>
<style scoped lang="scss">
@mixin text-styles {
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 150%;
  color: var(--white-color);
}

.app-footer {
  width: 100%;
  color: var(--secondary-text-color);
  background-color: var(--footer-bg-color);
  padding: 0;
  justify-content: center;

  .footer-inner {
    @include main-max-with;
    margin: auto;
    width: 100%;
    padding: 1.75rem 1.75rem 2.5rem;
  }

  .footer-logo {
    height: 2.75rem;
    width: 2.75rem;
    margin: 0.625rem auto 0.625rem 1.1875rem;
    color: var(--white-color);
  }

  .footer-nav-container {
    padding: 2.5rem;

    .footer-nav-list {
      display: flex;
      justify-content: flex-end;
      gap: 2.5rem;

      .footer-nav-list-col {
        li {
          text-align: left;
          padding: 0.375rem;
          width: 150px;

          .footer-link {
            @include text-styles;
            cursor: pointer;

            &:hover {
              font-weight: 700;
            }
          }

          &.footer-country {
            display: flex;
            align-items: center;
            gap: 0.3125rem;

            .country-flag {
              width: 1rem;
              height: 1rem;
            }
          }

          .theme-switcher {
            display: flex;
            align-items: center;
            gap: 0.3125rem;
            .label {
              @include text-styles;
              text-transform: capitalize;
            }

            .switch {
              width: 1.375rem;
              height: 0.75rem;

              .slider {
                box-shadow: none;
                background-color: var(--secondary-color);

                &::before {
                  width: 0.375rem;
                  height: 0.375rem;
                  top: 0.1875rem;
                  left: 0.1875rem;
                  background-color: var(--white-color);
                }
              }
              input:checked + .slider:before {
                transform: translateX(0.625rem);
              }
            }
          }
        }
      }
    }

    .footer-nav-list,
    .footer-nav-list-col {
      padding: 0;
      margin: 0;
      list-style: none;
    }
  }

  .report-bug-btn {
    justify-content: flex-end;
  }

  .footer-link,
  .footer-country-name,
  .footer-copyright {
    @include text-styles;
  }
}
@include breakpoint(small) {
  .app-footer {
    .footer-nav-container {
      padding-right: 0;
      padding-left: 0;
      .footer-nav-list {
        gap: 1.25rem;
      }
    }

    .footer-logo {
      margin-left: 0;
    }
  }
}
</style>
